import { ArrowRight, ArrowUpRight, ClipboardList, FileWarning, Lightbulb, Mail, Megaphone, UserCheck } from "lucide-react";
import { COMPLIANCE } from "@/lib/pricing";

const FLOW = [
  {
    icon: FileWarning,
    step: "01",
    title: "Report in InductIQ",
    desc: "Incidents and near misses are captured as structured InductIQ incident reports.",
  },
  {
    icon: Lightbulb,
    step: "02",
    title: "Turn it into a lesson",
    desc: "WorkSafeStory converts each report into a clear lesson learned: what happened, why, and what changes.",
  },
  {
    icon: UserCheck,
    step: "03",
    title: "Reviewed by a person",
    desc: "A person on the safety team reads and approves every lesson before any crew sees it.",
  },
  {
    icon: Megaphone,
    step: "04",
    title: "Teach it at the toolbox talk",
    desc: "Lessons become ready-to-run toolbox-talk content, so every crew hears what the last one learned.",
  },
];


function FlowStep({ s }) {
  return (
    <div className="bg-white border-2 border-[#09090B] p-8 ws-hard-shadow" data-testid={`lessons-step-${s.step}`}>
      <div className="flex items-start justify-between">
        <div className="w-12 h-12 bg-[#FF5A00] text-white flex items-center justify-center">
          <s.icon strokeWidth={2.5} className="w-6 h-6" />
        </div>
        <div className="font-display font-black text-5xl text-[#E4E4E7] tracking-tighter">{s.step}</div>
      </div>
      <h3 className="font-display font-bold text-2xl mt-6 tracking-tight">{s.title}</h3>
      <p className="text-[#52525B] mt-3 leading-relaxed text-base">{s.desc}</p>
    </div>
  );
}

export function SafetyTeams() {
  return (
    <div id="safety-teams" className="scroll-mt-20" data-testid="safety-teams-section">
      <LessonsHowItWorks />
      <ComplianceService />
    </div>
  );
}

function LessonsHowItWorks() {
  return (
    <section className="border-b-2 border-[#09090B] ws-grid-bg" data-testid="lessons-how-it-works">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-20">
        <div className="flex items-end justify-between flex-wrap gap-6">
          <div>
            <div className="ws-label text-[#FF5A00]">/ 03 — For safety teams · How it works</div>
            <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tighter mt-3 max-w-3xl">
              From incident report to toolbox talk.
            </h2>
          </div>
          <a
            href="https://inductiq.ai"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="link-inductiq"
            className="ws-label inline-flex items-center gap-2 border-b-2 border-[#09090B] pb-1 hover:text-[#FF5A00] hover:border-[#FF5A00] transition-colors"
          >
            Explore InductIQ <ArrowUpRight strokeWidth={2.5} className="w-4 h-4" />
          </a>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
          {FLOW.map((s) => (
            <FlowStep key={s.step} s={s} />
          ))}
        </div>
      </div>
    </section>
  );
}

function ComplianceService() {
  return (
    <section className="border-b-2 border-[#09090B] bg-[#FFCC00]" data-testid="compliance-service">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-7">
          <div className="ws-label text-[#09090B] flex items-center gap-2">
            <ClipboardList strokeWidth={2.5} className="w-4 h-4" /> / 04 — Done-for-you
          </div>
          <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tighter mt-3">
            Done-for-you with our Compliance Service.
          </h2>
          <p className="mt-6 text-base md:text-lg text-[#09090B] max-w-xl leading-relaxed">
            Prefer not to run it yourself? Our team evaluates your site, then keeps your OSHA and
            environmental records documented and turns incidents and near misses into lessons
            learned and toolbox-talk content for you, every month.
          </p>
          <a
            href="mailto:info@wsafeai.com?subject=WorkSafeStory%20Compliance%20Service"
            data-testid="compliance-service-cta"
            className="mt-8 inline-flex items-center gap-3 bg-[#09090B] hover:bg-[#FF5A00] text-white font-bold px-8 py-4 text-xs uppercase tracking-[0.2em] border-2 border-[#09090B] ws-hard-shadow transition-colors"
          >
            <Mail strokeWidth={2.5} className="w-5 h-5" /> Email info@wsafeai.com
            <ArrowRight strokeWidth={2.5} className="w-5 h-5" />
          </a>
        </div>
        <div className="lg:col-span-5 bg-white border-2 border-[#09090B] p-6 ws-hard-shadow" data-testid="compliance-price-card">
          <div className="ws-label text-[#FF5A00]">Compliance Evaluation &amp; Documentation Service</div>
          <div className="font-display font-black text-3xl tracking-tighter mt-2">{COMPLIANCE.evaluation}</div>
          <div className="mt-4 grid grid-cols-3 gap-2 border-t-2 border-dashed border-[#09090B] pt-4">
            {COMPLIANCE.plans.map((p) => (
              <div key={p.name} data-testid={`compliance-tier-${p.name.toLowerCase()}`}>
                <div className="ws-label text-[#52525B]">{p.name}</div>
                <div className="font-display font-black text-2xl tracking-tighter">{p.price}</div>
              </div>
            ))}
          </div>
          <div className="ws-label text-[#52525B] mt-2">{COMPLIANCE.unit}</div>
          <ul className="mt-4 space-y-1 text-xs text-[#52525B] leading-relaxed">
            {COMPLIANCE.notes.map((n) => <li key={n}>{n}</li>)}
          </ul>
        </div>
      </div>
    </section>
  );
}
